/**
 * Microphone button component for voice recording
 */

import React, { useState, useRef, useEffect } from 'react';
import { AgeProfile } from '../config/ageProfiles.config';
import { VoiceLiveConversationState } from '../types/voiceLive.types';
import { VoiceLiveClient } from '../services/voiceLiveClient';
import { createAudioRecorder } from '../utils/audioUtils';
import { logger } from '../utils/logger';
import { MicrophoneIcon, MicrophoneOffIcon, VolumeIcon, AlertIcon, RefreshIcon } from './SvgConsolidated/SvgIcons';

interface MicButtonProps {
  isRecording: boolean;
  conversationState: VoiceLiveConversationState;
  ageProfile: AgeProfile;
  voiceLiveClient: VoiceLiveClient;
  isListening?: boolean;
  onToggleListening?: () => void;
  isContinuousMode?: boolean;
  onPronunciationFrame?: (pcmData: ArrayBuffer) => void;
}

export default function MicButton({ 
  isRecording, 
  conversationState, 
  ageProfile, 
  voiceLiveClient, 
  isListening = false,
  onToggleListening,
  isContinuousMode = false,
  onPronunciationFrame
}: MicButtonProps) {
  const [isPressed, setIsPressed] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isMicMutedByAssistant, setIsMicMutedByAssistant] = useState(false);
  const audioRecorderRef = useRef<any>(null);

  // Verify onPronunciationFrame prop is received
  useEffect(() => {
    console.log('🔍 [MicButton.useEffect] Component mounted/updated');
    console.log(`🔍 [MicButton.useEffect] onPronunciationFrame prop: ${onPronunciationFrame ? 'DEFINED ✅' : 'UNDEFINED ❌'}`);
    console.log(`🔍 [MicButton.useEffect] onPronunciationFrame type: ${typeof onPronunciationFrame}`);
    if (onPronunciationFrame) {
      console.log(`🔍 [MicButton.useEffect] onPronunciationFrame function: ${onPronunciationFrame.toString().substring(0, 100)}...`);
    }
  }, [onPronunciationFrame]);

  // Request microphone permission on component mount
  useEffect(() => {
    const requestPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,  // ✅ Browser-level acoustic echo cancellation (AEC)
            noiseSuppression: true,  // ✅ Reduces background noise
            autoGainControl: true    // ✅ Keeps volume levels consistent
          }
        });
        setHasPermission(true);
        stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
        logger.logAudioEvent('microphone_permission_granted', {
          echo_cancellation: true,
          noise_suppression: true,
          auto_gain_control: true
        }, ageProfile.age_range.min);
      } catch (error) {
        setHasPermission(false);
        logger.logErrorEvent('microphone_permission_denied', error as Error, {}, ageProfile.age_range.min);
      }
    };

    requestPermission();
  }, [ageProfile.age_range.min]);

  // Listen for echo cancellation mic mute state changes
  useEffect(() => {
    const handleMicMutedStateChange = (isMuted: boolean) => {
      setIsMicMutedByAssistant(isMuted);
      logger.logAudioEvent(
        isMuted ? 'mic_muted_by_assistant' : 'mic_unmuted_by_assistant',
        { isMuted },
        ageProfile.age_range.min
      );
    };

    voiceLiveClient.addEventListener('micMutedStateChange', handleMicMutedStateChange);

    return () => {
      voiceLiveClient.removeEventListener('micMutedStateChange', handleMicMutedStateChange);
    };
  }, [voiceLiveClient, ageProfile.age_range.min]);

  const startRecording = async () => {
    if (!hasPermission) {
      return;
    }

    try {
      console.log('🎤 [MicButton] Starting recording...');
      console.log(`🎤 [MicButton] onPronunciationFrame callback: ${onPronunciationFrame ? 'DEFINED ✅' : 'UNDEFINED ❌'}`);
      
      const audioRecorder = await createAudioRecorder({
        onDataAvailable: (base64Data: string) => {
          voiceLiveClient.sendAudioData(base64Data);
        },
        onPronunciationFrame: onPronunciationFrame,
        onStop: () => {
          // Azure Semantic VAD automatically commits audio when speech end is detected
          // Manual commit is not supported and causes errors
          // voiceLiveClient.commitAudioBuffer(); // REMOVED - not compatible with Semantic VAD
        }
      });

      audioRecorderRef.current = audioRecorder;
      audioRecorder.start();
      
      logger.logAudioEvent('recording_started', {}, ageProfile.age_range.min);
    } catch (error) {
      logger.logErrorEvent('recording_start_failed', error as Error, {}, ageProfile.age_range.min);
    }
  };

  const stopRecording = () => {
    if (audioRecorderRef.current) {
      audioRecorderRef.current.stop();
      audioRecorderRef.current = null;
      logger.logAudioEvent('recording_stopped', {}, ageProfile.age_range.min);
    }
  };

  const handleClick = () => {
    if (!voiceLiveClient.isConnected()) {
      return;
    }

    if (isContinuousMode && onToggleListening) {
      // In continuous mode, toggle listening state
      onToggleListening();
    } else if (conversationState === 'idle') {
      // In push-to-talk mode, toggle recording on/off with single click
      if (!isPressed) {
        setIsPressed(true);
        startRecording();
      } else {
        setIsPressed(false);
        stopRecording();
      }
    } else if (conversationState === 'listening' || isPressed) {
      // Stop recording if currently recording
      setIsPressed(false);
      stopRecording();
    }
  };

  const handleMouseDown = () => {
    if (isContinuousMode) {
      // In continuous mode, don't use push-to-talk
      return;
    }
    
    if (!voiceLiveClient.isConnected() || conversationState !== 'idle') {
      return;
    }
    
    setIsPressed(true);
    startRecording();
  };

  const handleMouseUp = () => {
    if (isContinuousMode) {
      // In continuous mode, don't use push-to-talk
      return;
    }
    
    setIsPressed(false);
    stopRecording();
  };

  // const _handleTouchStart = (_e: React.TouchEvent) => {
  //   _e.preventDefault();
  //   if (!isContinuousMode) {
  //     handleMouseDown();
  //   }
  // };

  // const _handleTouchEnd = (e: React.TouchEvent) => {
  //   e.preventDefault();
  //   if (!isContinuousMode) {
  //     handleMouseUp();
  //   }
  // };

  const getButtonState = () => {
    if (!hasPermission) return 'permission';
    if (!voiceLiveClient.isConnected()) return 'disconnected';
    
    if (isContinuousMode) {
      // In continuous mode, show listening state based on isListening prop
      if (conversationState === 'listening') return 'listening';
      if (conversationState === 'processing') return 'processing';
      if (conversationState === 'speaking') return 'speaking';
      if (conversationState === 'error') return 'error';
      if (isListening) return 'continuous_listening';
      return 'continuous_paused';
    } else {
      // In push-to-talk mode, use original logic
      if (conversationState === 'listening' || isPressed) return 'recording';
      if (conversationState === 'processing') return 'processing';
      if (conversationState === 'speaking') return 'speaking';
      if (conversationState === 'error') return 'error';
      return 'ready';
    }
  };

  const getButtonContent = () => {
    const state = getButtonState();
    
    switch (state) {
      case 'permission':
        return {
          text: ageProfile.content.statusMessages.microphonePermission,
          icon: (
            <MicrophoneOffIcon className="w-8 h-8" />
          ),
          className: 'bg-red-500 text-white cursor-not-allowed'
        };
      
      case 'disconnected':
        return {
          text: 'Not Connected',
          icon: (
            <MicrophoneOffIcon className="w-8 h-8" />
          ),
          className: 'bg-gray-500 text-white cursor-not-allowed'
        };

      case 'recording':
        return {
          text: 'Listening...', // Show "Listening..." instead of "All Done!" when actively recording
          icon: (
            <div className="relative">
              <MicrophoneIcon className="w-8 h-8 animate-pulse" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
            </div>
          ),
          className: 'bg-red-500 text-white animate-pulse'
        };

      case 'listening':
        return {
          text: 'Listening...',
          icon: (
            <div className="relative">
              <MicrophoneIcon className="w-8 h-8 animate-pulse" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
            </div>
          ),
          className: 'bg-green-500 text-white animate-pulse'
        };

      case 'continuous_listening':
        return {
          text: 'Listening - Tap to Pause',
          icon: (
            <div className="relative">
              <MicrophoneIcon className="w-8 h-8" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          ),
          className: 'bg-green-500 text-white'
        };

      case 'continuous_paused':
        return {
          text: 'Paused - Tap to Listen',
          icon: (
            <div className="relative">
              <MicrophoneIcon className="w-8 h-8 opacity-60" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full"></div>
            </div>
          ),
          className: 'bg-gray-400 text-white'
        };

      case 'processing':
        return {
          text: 'Processing...',
          icon: (
            <div className="animate-spin">
              <RefreshIcon className="w-8 h-8" />
            </div>
          ),
          className: 'bg-yellow-500 text-white cursor-wait'
        };

      case 'speaking':
        return {
          text: 'Speaking...',
          icon: (
            <div className="animate-bounce">
              <VolumeIcon className="w-8 h-8" />
            </div>
          ),
          className: 'bg-blue-500 text-white cursor-wait'
        };

      case 'error':
        return {
          text: 'Error - Retry',
          icon: (
            <AlertIcon className="w-8 h-8" />
          ),
          className: 'bg-red-600 text-white'
        };

      case 'ready':
      default:
        return {
          text: ageProfile.content.buttonLabels.startRecording,
          icon: (
            <MicrophoneIcon className="w-8 h-8" />
          ),
          className: `hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl`
        };
    }
  };

  const buttonContent = getButtonContent();
  const canInteract = hasPermission && voiceLiveClient.isConnected() && 
                    (conversationState === 'idle' || conversationState === 'error' || isContinuousMode);

  return (
    <div className="flex flex-col items-center gap-4 pt-32">
      <div className="relative">
        <button
          className={`
            ${ageProfile.theme.buttonSize.width} 
            ${ageProfile.theme.buttonSize.height}
            ${ageProfile.theme.borderRadius}
            ${buttonContent.className}
            flex flex-col items-center justify-center
            font-semibold
            ${ageProfile.theme.spacing.touchTargets}
            select-none
            ${canInteract ? 'cursor-pointer' : 'cursor-not-allowed'}
            ${(isListening || conversationState === 'listening') ? 'opacity-70' : 'opacity-100'}
            transition-opacity duration-200
          `}
          style={{
            backgroundColor: canInteract ? ageProfile.theme.colors.primary : undefined,
            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
            touchAction: 'manipulation',
            minWidth: '112px',
            minHeight: '112px'
          }}
          onClick={canInteract ? handleClick : undefined}
          disabled={!canInteract}
        >
          <div className="mb-2">
            {buttonContent.icon}
          </div>
        </button>
        
        {/* Echo cancellation indicator - show when mic is muted by assistant */}
        {isMicMutedByAssistant && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-500 bg-opacity-80 rounded-full pointer-events-none">
            <MicrophoneOffIcon className="w-12 h-12 text-white" />
          </div>
        )}
      </div>
      
      <div className="text-center max-w-xs">
        <p className={`${ageProfile.theme.typography.bodySize} text-gray-700 font-medium`}>
          {isMicMutedByAssistant ? 'Muted (Assistant Speaking)' : buttonContent.text}
        </p>
        {getButtonState() === 'ready' && !isContinuousMode && !isMicMutedByAssistant && (
          <p className="text-sm text-gray-500 mt-1">
            Tap to talk
          </p>
        )}
        {getButtonState() === 'recording' && !isContinuousMode && !isMicMutedByAssistant && (
          <p className="text-sm text-gray-500 mt-1">
            Tap to stop
          </p>
        )}
        {isContinuousMode && !isMicMutedByAssistant && (
          <p className="text-sm text-gray-500 mt-1">
            {isListening ? "AI is listening for your voice" : "Tap to resume listening"}
          </p>
        )}
        {isMicMutedByAssistant && (
          <p className="text-sm text-red-600 mt-1">
            🔇 Echo cancellation active
          </p>
        )}
      </div>
    </div>
  );
}